import axios from 'axios';
import { apiClient } from '../client';
import { tokenStore } from '../../auth/tokenStore';
import { DEV_DEFAULTS } from '../../config/devDefaults';
import { authEndpoints, sessionEndpoints } from '../endpoints/auth.endpoints';
import type {
  AccessibleApplication,
  AccessibleTenant,
  AuthenticateRequest,
  AuthenticateResponse,
  DiscoverRequest,
  DiscoverResponse,
  LoginRequest,
  RolePermissionMerged,
  TenantHubApiEnvelope,
} from '../types/auth.types';
import type { ApiResponse } from '../types/envelope.types';
import type {
  AuthProperty,
  LoginResponse,
  ModulePermission,
  SessionContext,
} from '../../auth/session.types';
import { propertyService } from './propertyService';

/*
 * Login flow (mirrors crs-web's discover → authenticate → enrich pipeline):
 *
 *   1. POST /auth/discover                → BFF proxies to TenantHub
 *   2. Pick tenant + app (auto if only one; otherwise caller throws so the UI
 *      can render a picker on a future revision)
 *   3. POST /auth/authenticate            → BFF proxies to TenantHub, returns JWT
 *   4. GET  /auth/role-permissions/by-roles → BFF proxies to crs-api
 *   5. Filter DB-driven modules for MOB-* prefix + map into SessionContext
 *
 * Fail-closed per the RBAC standard (crs/.claude/CLAUDE.md → RBAC section):
 *   - No hardcoded fallback modules — if the DB has no MOB-* rows for the
 *     user's role, login is rejected with a clear message.
 *   - No secondary discover / bootstrap synthesis — recovery is via re-running
 *     the seed fix (crs-db/migrations/fixes/018_seed_mobile_modules_in_role_permissions.sql).
 */

const MOB_MODULE_PREFIX = 'MOB-';

/** Envelope helper — turns axios failures into readable Error messages. */
function throwFromEnvelope(err: unknown, fallback: string): never {
  if (axios.isAxiosError(err) && err.response?.data) {
    const body = err.response.data as { message?: string };
    throw new Error(body?.message || err.message || fallback);
  }
  throw err instanceof Error ? err : new Error(fallback);
}

async function discover(req: DiscoverRequest): Promise<DiscoverResponse> {
  try {
    const res = await apiClient.post<TenantHubApiEnvelope<DiscoverResponse>>(
      authEndpoints.discover(),
      req,
    );
    const body = res.data;
    if (!body.success || !body.data) {
      throw new Error(body.message || 'Login failed — no tenants available');
    }
    return body.data;
  } catch (err) {
    throwFromEnvelope(err, 'Login failed');
  }
}

async function authenticate(req: AuthenticateRequest): Promise<AuthenticateResponse> {
  try {
    const res = await apiClient.post<TenantHubApiEnvelope<AuthenticateResponse>>(
      authEndpoints.authenticate(),
      req,
    );
    const body = res.data;
    if (!body.success || !body.data) {
      throw new Error(body.message || 'Authentication failed');
    }
    return body.data;
  } catch (err) {
    throwFromEnvelope(err, 'Authentication failed');
  }
}

async function fetchRolePermissions(
  roles: string[],
  tenantId: string | null,
): Promise<RolePermissionMerged> {
  if (roles.length === 0) {
    return { visibleModules: [], moduleActions: {} };
  }
  try {
    const csv = roles.join(',');
    // Explicitly attach X-Tenant-ID even though this endpoint lives under
    // /api/v1/mobile/* (excluded from BFF's TenantMiddleware). Belt-and-suspenders
    // — if the BFF binary hasn't been rebuilt with the exclusion yet, the header
    // still lets tenant validation pass instead of failing with MISSING_TENANT_ID.
    const explicitTenantId = tenantId || DEV_DEFAULTS.tenantId;
    const res = await apiClient.get<ApiResponse<RolePermissionMerged>>(
      `${authEndpoints.rolePermissionsByRoles()}?roles=${encodeURIComponent(csv)}`,
      explicitTenantId
        ? { headers: { 'X-Tenant-ID': explicitTenantId } }
        : undefined,
    );
    const body = res.data;
    if (!body.success || !body.data) {
      throw new Error(body.message || 'Role permissions lookup failed');
    }
    return body.data;
  } catch (err) {
    throwFromEnvelope(err, 'Role permissions lookup failed');
  }
}

/**
 * Bare role names TenantHub returns in `userRole` (system, admin,
 * booking_agent, user) that we normalize to their crs_-prefixed seed
 * codes (crs_system, crs_admin, crs_booking_agent, crs_user).
 * `admin.tbl_role_permissions` stores the prefixed form; if we send the
 * bare form, `RolePermissions/by-roles` matches nothing and returns
 * `sourceRoles: []` + empty modules.
 */
const CRS_ROLE_ALIASES: Record<string, string> = {
  system:        'crs_system',
  admin:         'crs_admin',
  booking_agent: 'crs_booking_agent',
  bookingagent:  'crs_booking_agent',
  user:          'crs_user',
};

/**
 * Roles a JWT can carry, extracted for the RolePermissions/by-roles lookup.
 * Reads (in this order) the `roles[]`, `role`, `realm_access.roles[]`, and
 * `resource_access.<any>.roles[]` claims — plus the TenantHub `userRole`
 * fallback. Each collected value is normalized to the seeded `crs_*` form
 * when it matches a known bare alias (TenantHub strips the prefix from
 * `userRole` for display; the seed table keeps it).
 */
function extractRoles(accessToken: string, fallback: string | null): string[] {
  const collected = new Set<string>();
  const add = (raw: string) => {
    const t = raw.trim();
    if (!t) return;
    collected.add(t);
    const alias = CRS_ROLE_ALIASES[t.toLowerCase()];
    if (alias) collected.add(alias);
  };

  if (fallback) add(fallback);
  try {
    const [, payload] = accessToken.split('.');
    if (!payload) return Array.from(collected);
    // Base64URL → Base64 → JSON
    const b64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const json = JSON.parse(atob(b64.padEnd(b64.length + ((4 - (b64.length % 4)) % 4), '=')));
    const push = (v: unknown) => {
      if (typeof v === 'string' && v.trim()) add(v);
      else if (Array.isArray(v)) v.forEach(push);
    };
    push(json.role);
    push(json.roles);
    push(json?.realm_access?.roles);
    if (json?.resource_access && typeof json.resource_access === 'object') {
      for (const value of Object.values(json.resource_access as Record<string, unknown>)) {
        push((value as { roles?: unknown })?.roles);
      }
    }
  } catch {
    // JWT unreadable — fall back to whatever was passed in.
  }
  return Array.from(collected);
}

/** Only tenant→application combinations that expose at least one application. */
function pickAutoSelection(
  tenants: AccessibleTenant[],
): { tenant: AccessibleTenant; application: AccessibleApplication } | null {
  const eligible = tenants.filter((t) => t.applications.length > 0);
  if (eligible.length !== 1) return null;
  const [tenant] = eligible;
  if (tenant.applications.length !== 1) return null;
  return { tenant, application: tenant.applications[0] };
}

/**
 * Fetches the tenant's real properties from the BFF so `selectedPropertyId`
 * points at an actual `property.tbl_property.id` — NOT the tenantId. Without
 * this call, `assembleSession` fell back to using `auth.tenantId` as a fake
 * property entity, and every property-scoped query on the home screen
 * (occupancy, arrivals, meals, in-house) filtered by `property_id =
 * <tenantId>` → zero rows.
 *
 * Soft-fails to `[]` on error (network / permission / no properties) so
 * login itself is not blocked — the fallback in `assembleSession` keeps the
 * old behaviour for those cases; better than an infinite login screen.
 */
async function fetchAccessibleProperties(): Promise<AuthProperty[]> {
  try {
    const list = await propertyService.getAccessible();
    return list
      .filter((p) => p.isActive)
      .map((p) => ({
        entityId: p.entityId,
        name: p.name,
        timezone: p.timezone ?? 'UTC',
      }));
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn(
      '[authService] Failed to fetch accessible properties; falling back to tenantId as property entity.',
      err,
    );
    return [];
  }
}

function assembleSession(
  auth: AuthenticateResponse,
  perms: RolePermissionMerged,
  accessibleProperties: AuthProperty[],
): SessionContext {
  const mobileModules = perms.visibleModules.filter((m) => m.startsWith(MOB_MODULE_PREFIX));
  const actions: ModulePermission[] = Object.entries(perms.moduleActions)
    .filter(([moduleCode]) => moduleCode.startsWith(MOB_MODULE_PREFIX))
    .map(([moduleCode, moduleActions]) => ({ moduleCode, actions: moduleActions }));

  // Prefer the real property list from /mobile/properties. Fall back to the
  // legacy tenant-as-property placeholder only when the BFF returned no rows
  // (empty list OR call failed). The fallback keeps existing tenants without
  // property records unblocked, but yields the "all zeros home screen" bug
  // that motivated this whole change.
  const properties: AuthProperty[] = accessibleProperties.length > 0
    ? accessibleProperties
    : auth.tenantId
      ? [
          {
            entityId: auth.tenantId,
            name: auth.tenantDisplayName || auth.tenantName || auth.tenantCode || 'Property',
            timezone: 'UTC',
          },
        ]
      : [];

  const fullName = [auth.userFirstName, auth.userMiddleName, auth.userLastName]
    .filter((s) => s && s.trim().length > 0)
    .join(' ')
    .trim();

  return {
    user: {
      userId: auth.userId ?? auth.userName ?? 'unknown',
      username: auth.userName ?? '',
      fullName: fullName || auth.userName || '',
      email: auth.userEmail ?? null,
      roles: auth.userRole ? [auth.userRole] : [],
    },
    properties,
    defaultPropertyId: properties[0]?.entityId ?? null,
    selectedPropertyId: properties[0]?.entityId ?? null,
    mobileModules,
    actions,
  };
}

export const authService = {
  /**
   * Runs the full TenantHub 2-step login + DB-driven RBAC enrichment.
   * Preserves the existing SessionProvider.login signature so LoginScreen
   * still calls `login({ username, password })` — tenant/app auto-select
   * happens here when possible.
   */
  async login(req: LoginRequest): Promise<LoginResponse> {
    // Step 1 — discover accessible tenants / applications.
    const discovery = await discover({
      username: req.username,
      password: req.password,
      tenantId: req.tenantId,
    });

    if (discovery.accessibleTenants.length === 0) {
      throw new Error(
        'Your account has no accessible tenants. Please contact your administrator.',
      );
    }

    // Step 2 — pick a tenant + application. UI can pre-select via req; else
    // we auto-pick when the choice is unambiguous.
    let selectedTenantId = req.tenantId ?? null;
    let selectedApplicationId = req.applicationId ?? null;

    if (!selectedTenantId || !selectedApplicationId) {
      const auto = pickAutoSelection(discovery.accessibleTenants);
      if (auto) {
        selectedTenantId = auto.tenant.tenantId;
        selectedApplicationId = auto.application.applicationId;
      } else if (discovery.recommendedTenant && discovery.recommendedTenant.applications.length === 1) {
        selectedTenantId = discovery.recommendedTenant.tenantId;
        selectedApplicationId = discovery.recommendedTenant.applications[0].applicationId;
      } else {
        // Multiple tenants or multiple apps — needs a picker in the UI.
        // Surface a typed error the LoginScreen can catch to show a picker.
        throw Object.assign(
          new Error(
            'Please select a tenant and application to continue.',
          ),
          { code: 'DISCOVER_MULTIPLE', discovery },
        );
      }
    }

    // Step 3 — authenticate with the chosen tenant/app.
    const auth = await authenticate({
      username: req.username,
      password: req.password,
      selectedTenantId,
      selectedApplicationId,
    });

    // Step 4 — fetch DB-driven role permissions (fail-closed). Pass tenant
    // ID explicitly so the request carries X-Tenant-ID regardless of the
    // BFF's middleware state.
    const roles = extractRoles(auth.accessToken, auth.userRole ?? null);
    const perms = await fetchRolePermissions(roles, auth.tenantId);

    const mobileVisible = perms.visibleModules.filter((m) => m.startsWith(MOB_MODULE_PREFIX));
    if (mobileVisible.length === 0) {
      // Distinguish two failure modes for a clearer support message.
      const displayRole = auth.userRole || roles.join(', ') || 'unknown';
      throw new Error(
        perms.visibleModules.length === 0
          ? `Your role "${displayRole}" is not registered in the CRS role table. ` +
            `Ask an administrator to add MOB-* permissions for this role.`
          : `Your role "${displayRole}" has no mobile modules enabled ` +
            `(only desktop modules are assigned). Ask an administrator to enable mobile access.`,
      );
    }

    // Step 5 — persist tenant + access-token context (used by axiosClient in
    // later calls). Access token MUST be set BEFORE the properties fetch below
    // because that call goes through the standard axios pipeline and needs
    // Authorization + X-Tenant-ID headers.
    //
    // NOTE: `X-App-ID` is deliberately pinned to the env-configured client ID
    // (DEV_DEFAULTS.appId ← VITE_APP_ID) for the entire session. The
    // `applicationId` TenantHub returns from /authenticate can be a different
    // GUID (TenantHub's internal registration ID vs. the Keycloak client ID
    // we discover with), and letting it drift on subsequent requests breaks
    // the crs-api / TenantHub round-trip that expects the Keycloak client ID.
    if (auth.tenantId) tokenStore.setTenantId(auth.tenantId);
    tokenStore.setAccessToken(auth.accessToken);

    // Step 6 — fetch the tenant's real properties so `selectedPropertyId`
    // points at an actual property.tbl_property.id, not the tenantId.
    const accessibleProperties = await fetchAccessibleProperties();

    const session = assembleSession(auth, perms, accessibleProperties);
    return {
      accessToken: auth.accessToken,
      refreshToken: auth.refreshToken ?? '',
      expiresInSeconds: auth.expiresIn ?? 900,
      session,
    };
  },

  async logout(): Promise<void> {
    try {
      await apiClient.post(authEndpoints.logout());
    } catch {
      // Best-effort — local cleanup happens regardless.
    }
  },

  async getSessionContext(): Promise<SessionContext> {
    try {
      const res = await apiClient.get<ApiResponse<SessionContext>>(
        sessionEndpoints.context(),
      );
      const body = res.data;
      if (!body.success || !body.data) {
        throw new Error(body.message || 'Failed to load session');
      }
      return body.data;
    } catch (err) {
      throwFromEnvelope(err, 'Failed to load session');
    }
  },
};
